/**
 * File Upload Component
 * Reusable component for uploading files with drag-and-drop support
 */

import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Button, Typography, LinearProgress, Alert, IconButton, List,
    ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction, Paper,
    Chip, Card, CardMedia, CardContent, CardActions
} from '@mui/material';
import {
    CloudUpload, Description, Image, DeleteOutline, CheckCircle, Error,
    CameraAlt, GpsFixed, CloudQueue, WifiOff
} from '@mui/icons-material';
import api from '../services/api';
import { Capacitor } from '@capacitor/core';
import offlineCameraService from '../services/offlineCameraService';

const FileUpload = ({
    uploadType = 'documents', // 'documents', 'portfolio', 'profile-picture'
    accept,
    multiple = false,
    maxFiles = 1,
    maxSize = 10 * 1024 * 1024, // 10MB default
    onUploadComplete,
    onUploadError,
    additionalData = {},
    buttonText = 'Upload File',
    showPreview = true,
    autoUpload = false // Auto-upload files after selection
}) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const fileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);

    // AUGMENTED: Offline camera support
    const [offlinePhotos, setOfflinePhotos] = useState([]);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [pendingCount, setPendingCount] = useState(0);
    const [capturing, setCapturing] = useState(false);

    // AUGMENTED: Load offline photos on mount
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            loadOfflinePhotos();
            updatePendingCount();
        }
    }, []);

    // AUGMENTED: Monitor network status
    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Auto-upload when files are selected
    useEffect(() => {
        const uploadFiles = async () => {
            if (!autoUpload || files.length === 0 || uploading) {
                return;
            }

            setUploading(true);
            setError('');
            setSuccess('');
            setUploadProgress(0);

            try {
                const formData = new FormData();

                if (multiple) {
                    files.forEach(file => {
                        formData.append('images', file);
                    });
                } else {
                    formData.append(uploadType === 'portfolio' ? 'images' : uploadType === 'profile-picture' ? 'image' : 'file', files[0]);
                }

                // Add additional data
                Object.keys(additionalData).forEach(key => {
                    formData.append(key, additionalData[key]);
                });

                const response = await api.post(
                    `/uploads/${uploadType}`,
                    formData,
                    {
                        onUploadProgress: (progressEvent) => {
                            const percentCompleted = Math.round(
                                (progressEvent.loaded * 100) / progressEvent.total
                            );
                            setUploadProgress(percentCompleted);
                        }
                    }
                );

                setSuccess(response.data.message || 'Upload successful!');
                
                if (onUploadComplete) {
                    onUploadComplete(response.data.data);
                }

                // Don't clear files in auto-upload mode to show preview
                setUploadProgress(100);

                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

            } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Upload failed';
                setError(errorMessage);

                if (onUploadError) {
                    onUploadError(err);
                }
            } finally {
                setUploading(false);
            }
        };

        uploadFiles();
    }, [files]); // Only trigger when files change

    // AUGMENTED: Load offline photos from IndexedDB
    const loadOfflinePhotos = async () => {
        try {
            const photos = await offlineCameraService.getOfflinePhotos(
                additionalData.verificationId || null
            );
            setOfflinePhotos(photos);
        } catch (error) {
            console.error('Failed to load offline photos:', error);
        }
    };

    // AUGMENTED: Update pending upload count
    const updatePendingCount = async () => {
        try {
            const count = await offlineCameraService.getPendingCount();
            setPendingCount(count);
        } catch (error) {
            console.error('Failed to get pending count:', error);
        }
    };

    // AUGMENTED: Handle camera capture
    const handleCameraCapture = async () => {
        setCapturing(true);
        setError('');

        try {
            const result = await offlineCameraService.capturePhoto({
                verificationId: additionalData.verificationId,
                projectId: additionalData.projectId
            });

            setSuccess(result.message);

            // Reload offline photos
            await loadOfflinePhotos();
            await updatePendingCount();

            if (onUploadComplete && result.photo.uploaded) {
                onUploadComplete(result.photo);
            }

        } catch (err) {
            const errorMessage = err.message || 'Camera capture failed';
            setError(errorMessage);

            if (onUploadError) {
                onUploadError(err);
            }
        } finally {
            setCapturing(false);
        }
    };

    // AUGMENTED: Delete offline photo
    const handleDeleteOfflinePhoto = async (photoId) => {
        try {
            await offlineCameraService.deletePhoto(photoId);
            await loadOfflinePhotos();
            await updatePendingCount();
            setSuccess('Photo deleted');
        } catch (error) {
            setError('Failed to delete photo');
        }
    };

    // AUGMENTED: Manual sync trigger
    const handleManualSync = async () => {
        setUploading(true);
        setError('');

        try {
            const result = await offlineCameraService.syncPendingPhotos();
            setSuccess(result.message);
            await loadOfflinePhotos();
            await updatePendingCount();
        } catch (error) {
            setError('Sync failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = (selectedFiles) => {
        const fileList = Array.from(selectedFiles);
        
        // Validate file count
        if (!multiple && fileList.length > 1) {
            setError('Please select only one file');
            return;
        }
        
        if (fileList.length > maxFiles) {
            setError(`Maximum ${maxFiles} file(s) allowed`);
            return;
        }
        
        // Validate file sizes
        const invalidFiles = fileList.filter(file => file.size > maxSize);
        if (invalidFiles.length > 0) {
            setError(`File size must be less than ${(maxSize / (1024 * 1024)).toFixed(0)}MB`);
            return;
        }
        
        setFiles(fileList);
        setError('');
        setSuccess('');
    };

    
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };
    
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files);
        }
    };
    
    const handleUpload = async () => {
        if (files.length === 0) {
            setError('Please select a file');
            return;
        }
        
        setUploading(true);
        setError('');
        setSuccess('');
        setUploadProgress(0);
        
        try {
            const formData = new FormData();
            
            if (multiple) {
                files.forEach(file => {
                    formData.append('images', file);
                });
            } else {
                formData.append(uploadType === 'portfolio' ? 'images' : uploadType === 'profile-picture' ? 'image' : 'file', files[0]);
            }
            
            // Add additional data
            Object.keys(additionalData).forEach(key => {
                formData.append(key, additionalData[key]);
            });
            
            const response = await api.post(
                `/uploads/${uploadType}`,
                formData,
                {
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    }
                }
            );
            
            setSuccess(response.data.message || 'Upload successful!');
            setFiles([]);
            setUploadProgress(0);
            
            if (onUploadComplete) {
                onUploadComplete(response.data.data);
            }
            
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Upload failed';
            setError(errorMessage);
            
            if (onUploadError) {
                onUploadError(err);
            }
        } finally {
            setUploading(false);
        }
    };
    
    const handleRemoveFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
    };
    
    const getFileIcon = (file) => {
        if (file.type.startsWith('image/')) {
            return <Image color="primary" />;
        }
        return <Description color="action" />;
    };
    
    return (
        <Box>
            {/* Drag and Drop Area */}
            <Paper
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                sx={{
                    border: 2,
                    borderStyle: 'dashed',
                    borderColor: dragActive ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: dragActive ? 'action.hover' : 'background.paper',
                    transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    mb: 2
                }}
                onClick={() => fileInputRef.current?.click()}
            >
                <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                    Drag and drop {multiple ? 'files' : 'a file'} here, or click to select
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {accept && `Accepted formats: ${accept}`}
                    <br />
                    Maximum size: {(maxSize / (1024 * 1024)).toFixed(0)}MB
                    {multiple && maxFiles > 1 && ` • Maximum ${maxFiles} files`}
                </Typography>
                
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={(e) => handleFileSelect(e.target.files)}
                    style={{ display: 'none' }}
                />
            </Paper>

            {/* AUGMENTED: Camera Button for Mobile */}
            {Capacitor.isNativePlatform() && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<CameraAlt />}
                        onClick={handleCameraCapture}
                        disabled={capturing || uploading}
                        sx={{
                            background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)'
                        }}
                    >
                        {capturing ? 'Capturing...' : (isOffline ? 'Take Photo (Offline)' : 'Take Photo')}
                    </Button>
                    {pendingCount > 0 && (
                        <Button
                            variant="outlined"
                            startIcon={<CloudQueue />}
                            onClick={handleManualSync}
                            disabled={uploading || isOffline}
                        >
                            Sync ({pendingCount})
                        </Button>
                    )}
                </Box>
            )}

            {/* AUGMENTED: Offline Status Indicator */}
            {Capacitor.isNativePlatform() && isOffline && (
                <Alert severity="warning" icon={<WifiOff />} sx={{ mb: 2 }}>
                    Offline mode: Photos will be stored locally and uploaded when connection is restored.
                </Alert>
            )}

            {/* AUGMENTED: Offline Photos Preview */}
            {Capacitor.isNativePlatform() && offlinePhotos.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Captured Photos ({offlinePhotos.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                        {offlinePhotos.map((photo) => (
                            <Card key={photo.id} sx={{ minWidth: 200, maxWidth: 200 }}>
                                <CardMedia
                                    component="img"
                                    height="140"
                                    image={`data:image/${photo.format};base64,${photo.base64}`}
                                    alt="Captured photo"
                                />
                                <CardContent sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <GpsFixed sx={{ fontSize: 16, color: 'success.main' }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {photo.gpsQuality || 'GPS locked'}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={photo.uploaded ? 'Uploaded' : 'Pending'}
                                        size="small"
                                        color={photo.uploaded ? 'success' : 'warning'}
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {new Date(photo.timestamp).toLocaleTimeString()}
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ p: 1, pt: 0 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteOfflinePhoto(photo.id)}
                                        disabled={uploading}
                                    >
                                        <DeleteOutline fontSize="small" />
                                    </IconButton>
                                </CardActions>
                            </Card>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Selected Files List */}
            {files.length > 0 && showPreview && (
                <List sx={{ mb: 2 }}>
                    {files.map((file, index) => (
                        <ListItem key={index}>
                            <ListItemIcon>
                                {getFileIcon(file)}
                            </ListItemIcon>
                            <ListItemText
                                primary={file.name}
                                secondary={`${(file.size / 1024).toFixed(2)} KB`}
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    edge="end"
                                    onClick={() => handleRemoveFile(index)}
                                    disabled={uploading}
                                >
                                    <DeleteOutline />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}
            
            {/* Upload Progress */}
            {uploading && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Uploading... {uploadProgress}%
                    </Typography>
                </Box>
            )}
            
            {/* Success Message */}
            {success && (
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}
            
            {/* Error Message */}
            {error && (
                <Alert severity="error" icon={<Error />} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            {/* Upload Button - Only show if auto-upload is disabled */}
            {!autoUpload && (
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleUpload}
                    disabled={files.length === 0 || uploading}
                    startIcon={<CloudUpload />}
                >
                    {uploading ? 'Uploading...' : buttonText}
                </Button>
            )}
        </Box>
    );
};

export default FileUpload;

